// src/pages/Admin/Vote/AddVoteModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { FaSave, FaTimes, FaVoteYea, FaCalendarAlt } from "react-icons/fa";
import { createVote } from "../../../services/voteServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";

// Helpers
const pad = (n) => String(n).padStart(2, "0");
const toLocalDatetimeInput = (d = new Date()) => {
  const Y = d.getFullYear();
  const M = pad(d.getMonth() + 1);
  const D = pad(d.getDate());
  const h = pad(d.getHours());
  const m = pad(d.getMinutes());
  return `${Y}-${M}-${D}T${h}:${m}`;
};

const defaultForm = () => {
  const now = new Date();
  const in7 = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  return {
    title: "",
    description: "",
    start_time: toLocalDatetimeInput(now),
    end_time: toLocalDatetimeInput(in7),
    type: "vote", // "vote" | "sondage"
    is_active: true,
  };
};

export default function AddVoteModal({ isOpen, onClose, refreshList }) {
  const [formData, setFormData] = useState(defaultForm());
  const [submitting, setSubmitting] = useState(false);

  // Reset propre à l’ouverture
  useEffect(() => {
    if (isOpen) setFormData(defaultForm());
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Validation légère
  const errors = useMemo(() => {
    const list = {};
    if (!formData.title.trim()) list.title = "Titre requis.";
    if (!formData.start_time) list.start_time = "Date/heure de début requise.";
    if (!formData.end_time) list.end_time = "Date/heure de fin requise.";
    if (formData.start_time && formData.end_time) {
      const s = new Date(formData.start_time);
      const e = new Date(formData.end_time);
      if (e <= s) list.range = "La fin doit être postérieure au début.";
    }
    return list;
  }, [formData]);

  const canSubmit = Object.keys(errors).length === 0 && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      // Envoie tel quel (format "YYYY-MM-DDTHH:mm"), l’API peut parser une date locale.
      // Si besoin d’UTC ISO: new Date(formData.start_time).toISOString()
      await createVote({
        ...formData,
        title: formData.title.trim(),
        description: formData.description.trim(),
      });
      toast.success("Vote / Sondage ajouté avec succès");
      refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur lors de l'ajout :", error);
      toast.error("Erreur lors de l'ajout du vote");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay cliquable */}
      <div
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      {/* Modal */}
      <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl shadow-2xl bg-white dark:bg-slate-900 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
        {/* Header dégradé */}
        <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4">
          <div className="flex items-center justify-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center text-white">
              <FaVoteYea />
            </div>
            <h3 className="text-white text-lg sm:text-xl font-bold text-center uppercase">
              Ajouter un vote / sondage
            </h3>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5">
          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Titre *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Ex : Élection bureau 2025"
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              maxLength={120}
              required
            />
            {errors.title && (
              <p className="mt-1 text-xs text-rose-600">{errors.title}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Description (optionnelle)
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              placeholder="Quelques détails, consignes, critères…"
              className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              maxLength={1000}
            />
            <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 text-right">
              {formData.description.length}/1000
            </div>
          </div>

          {/* Ligne type + actif */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
                Type
              </label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
              >
                <option value="vote">Vote</option>
                <option value="sondage">Sondage</option>
              </select>
            </div>

            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/40 px-3 py-2">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="checkbox checkbox-sm"
                />
                <span className="text-sm text-slate-800 dark:text-slate-100">
                  Activer immédiatement
                </span>
              </label>
            </div>
          </div>

          {/* Période */}
          <div>
            <label className="block text-sm font-medium text-slate-800 dark:text-slate-100 mb-1">
              Période
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-1">
                  <FaCalendarAlt /> Début *
                </div>
                <input
                  type="datetime-local"
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  required
                />
                {errors.start_time && (
                  <p className="mt-1 text-xs text-rose-600">{errors.start_time}</p>
                )}
              </div>

              <div>
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-1">
                  <FaCalendarAlt /> Fin *
                </div>
                <input
                  type="datetime-local"
                  name="end_time"
                  value={formData.end_time}
                  onChange={handleChange}
                  min={formData.start_time || undefined}
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  required
                />
                {(errors.end_time || errors.range) && (
                  <p className="mt-1 text-xs text-rose-600">
                    {errors.end_time || errors.range}
                  </p>
                )}
              </div>
            </div>

            <p className="mt-2 text-[11px] text-slate-500 dark:text-slate-400">
              Astuce : les dates/horaires utilisent le fuseau horaire de votre navigateur.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              text="Annuler"
              icon={FaTimes}
              onClick={onClose}
              type="button"
              className="!bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 !rounded-xl gap-2"
            />
            <Button
              text={submitting ? "Enregistrement…" : "Enregistrer"}
              icon={FaSave}
              type="submit"
              disabled={!canSubmit}
              className={`!rounded-xl gap-2 ${!canSubmit ? "opacity-60 pointer-events-none" : ""}`}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
